export default `
uniform float uTime;
uniform float uRandomScalar;
uniform float uMouseMovementAmount;
uniform float uVerticalTilt;
uniform bool uIsFront;
uniform float uIndex;

varying vec2 vUv;
varying vec3 vNormal;
varying vec3 vNormal2;

varying vec3 vPosition;
varying vec3 vWorldPosition;

const float PI = 3.141592653589793;

mat2 rotate2D (float angle) {
  float s = sin(angle);
  float c = cos(angle);
  return mat2(c, -s, s, c);
}


vec3 center = vec3(0.0);
vec3 globalCenter = vec3(0.0);


float easeInCirc(float t) {
  return 1.0 - sqrt(1.0 - t * t);
}

float getLegProgress(float y) {
  float progress = smoothstep(0.0, 2.0, -y);
  return uIndex == 0.0 ? progress : progress * progress;
}

vec3 getCenter(float y, vec3 _center, float legProgress) {
  float phase = y * 2.0 - uTime * 2.5;

  float waveI = (uIsFront) ? 0.1 : 0.13;
  float waveI2 = waveI;

  waveI *= 1.2;

  waveI *= mix(1.0, 2.0, uMouseMovementAmount);
  waveI2 *= mix(0.0, 2.0, smoothstep(0.0, 0.2, uMouseMovementAmount));

  float waveF_z = 2.0;

  float circIndencity = 3.0;

  return _center + vec3(
    sin(-phase * 2.0) * 0.2 * waveI2 * legProgress,
    y,
    cos(phase * waveF_z) * waveI * legProgress
    -easeInCirc(y * 0.4) * waveI2 * circIndencity
  );
}

vec3 getWholeCenter(float y, float intensity) {
  return globalCenter + vec3(
    0.0,
    y,
    -easeInCirc(y * 0.6) * intensity
  );
}

float getWholeInverseSpeed(float y, float intensity) {
  float slope = intensity * y * 0.25 / sqrt(1.0 - y * y * 0.36);
  return inversesqrt(1.0 + slope * slope);
}

float getWholeCurveY(float signedLength, float intensity) {
  // Integrate dy/ds so the original Y distance becomes distance along the curve.
  const int arcLengthSteps = 32;
  float stepLength = signedLength / float(arcLengthSteps);
  float y = 0.0;
  for (int i = 0; i < arcLengthSteps; i++) {
    float midpointY = y + 0.5 * stepLength * getWholeInverseSpeed(y, intensity);
    y += stepLength * getWholeInverseSpeed(midpointY, intensity);
  }
  return y;
}

vec3 bendWhole(vec3 worldCenter, vec3 crossSectionOffset) {
  float intensity = 8.0;
  intensity *= mix(0.0, 0.8, smoothstep(0.0, 0.1, uMouseMovementAmount)) * uVerticalTilt;

  if (intensity == 0.0) return worldCenter + crossSectionOffset;

  float y = getWholeCurveY(worldCenter.y - globalCenter.y, intensity);

  vec3 distanceFromCenter = vec3(
    worldCenter.x - globalCenter.x,
    0.0,
    worldCenter.z - globalCenter.z
  );

  vec3 currentCenter = getWholeCenter(y, intensity);

  vec3 T = normalize(vec3(0.0, sqrt(1.0 - y * y * 0.25), -intensity * y * 0.25));
  vec3 B = normalize(cross(vec3(1.0, 0.0, 0.0), T));
  vec3 N = normalize(cross(T, B));

  return currentCenter
    + N * (distanceFromCenter.x + crossSectionOffset.x)
    + B * (distanceFromCenter.z + crossSectionOffset.z)
    + T * crossSectionOffset.y;
}


void main(){
  vUv = uv;
  vNormal = normal;
  vNormal2 = normalize(normalMatrix * normal);
  vPosition = position;
  vec3 pos = position;

  pos.y *= mix(1.0, 0.9, uMouseMovementAmount);

  float legProgress = getLegProgress(pos.y);

  // Offset from the original centerline
  vec3 distanceFromCenter = vec3(
    pos.x - center.x,
    0.0,
    pos.z - center.z
  );

  float eps = 0.05;

  // Deformed centerline
  vec3 prevCenter = getCenter(pos.y - eps, center, getLegProgress(pos.y - eps));
  vec3 currentCenter = getCenter(pos.y, center, legProgress);
  vec3 nextCenter = getCenter(pos.y + eps, center, getLegProgress(pos.y + eps));

  // Tangent
  vec3 T = normalize(nextCenter - prevCenter);

  // Local coordinate frame
  vec3 B = normalize(cross(vec3(1.0, 0.0, 0.0), T));
  vec3 N = normalize(cross(T, B));

  // Reconstruct the tube
  vec3 transformed = currentCenter
    + N * distanceFromCenter.x
    + B * distanceFromCenter.z;

  pos = transformed;

  pos.zy = rotate2D(0.05) * pos.zy;
  pos.xz = rotate2D(((uIndex) / 5.0) * 2.0 * PI) * pos.xz;

  currentCenter.zy = rotate2D(0.05) * currentCenter.zy;
  currentCenter.xz = rotate2D(((uIndex) / 5.0) * 2.0 * PI) * currentCenter.xz;



  // Bend the centerline, then rotate the whole cross-section without remapping its vertices individually.
  pos = bendWhole(currentCenter, pos - currentCenter);

  vec4 worldPos = modelMatrix * vec4(pos, 1.0);
  vec4 screenPos = projectionMatrix * viewMatrix * worldPos;
  vWorldPosition = worldPos.xyz;

  gl_Position = screenPos;
}
`
